import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { NavLink, useNavigate } from 'react-router-dom';
import Avatar from '../../../assets/images/avatar.png';
import './DashboardHeader.scss';
import {
  getSubscription,
  resetFetchData,
  resetUserData,
  setUserData,
} from '../../../redux/slices/LoginSlice';
import ConfirmationPopup from '../../../utils/ConfirmationPopup/ConfirmationPopup';
import axiosCall from '../../../utils/axiosCall';
import {
  showToastMessage,
  toastDefaults,
  ToastType,
} from '../../../utils/ToastOptionCommon';
import { toast } from 'react-toastify';
import { usePermissions } from '../../../utils/Permission/Permission';
import Loader from '../../CommonComponents/Loader/Loader';
import { AppDispatch } from '../../../redux/store';
import { useSocket } from '../../../context/SocketProvider';
import ImageEditorHOC from '../../../utils/ImageEditor/hoc/ImageEditorHOC';
import ImageEditorPopup from '../../../utils/ImageEditor/hoc/ImageEditorPopup';
import { GiPartyPopper } from 'react-icons/gi';
import CongratsPopup from '../../PopupComponents/Congrats/CongratsPopup';
import { useSubscription } from '../../../utils/CustomHooks/Subscription';
import { FaCalendarCheck, FaTriangleExclamation } from 'react-icons/fa6';

type IProps = {
  hideListingBtn?: boolean;
  showPopup: boolean;
  showHandler: () => void;
};

function DashboardHeader(props: IProps) {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();

  const { userData, isSubscription } = useSelector((state: any) => state.login);
  const [doLogout, setDoLogout] = useState(false);
  const [totalNotificationCount, setTotalNotificationCount] = useState<number>(
    () => {
      const cachedCount = sessionStorage.getItem('unread_count');
      return cachedCount ? parseInt(cachedCount, 10) : 0;
    }
  );
  const [loading, setLoading] = useState(false);
  const userImage = userData?.profileImage?.length
    ? userData?.profileImage
    : Avatar;
  const socket = useSocket();
  const [showCongratsPopup, setShowCongratsPopup] = useState(false);
  const { freeTrialDays, isActive, leftDays } = useSubscription();

  useEffect(() => {
    if (!socket) return;

    const handleNotificationCount = (count: number) => {
      const validCount = count || 0;
      setTotalNotificationCount(validCount);
      sessionStorage.setItem('unread_count', String(validCount));
    };

    socket.on('update-total-notification-count', handleNotificationCount);

    const userId = localStorage.getItem('userId');
    if (userId) {
      socket.emit('get-unread-count', userId);
    }

    return () => {
      socket.off('update-total-notification-count', handleNotificationCount);
    };
  }, []);

  useEffect(() => {
    dispatch(getSubscription({}));
  }, []);

  useEffect(() => {
    if (isSubscription) {
      dispatch(resetFetchData());
    }
  }, [isSubscription]);

  const handleLogout = async () => {
    try {
      setLoading(true);
      const userID = userData?._id;
      dispatch(resetUserData(null));
      localStorage.clear();
      socket.emit('logout-me', userData._id);
      setDoLogout(false);
      navigate('/');
      await axiosCall.post('/auth/logout/' + userID);
    } catch (error) {
      toast.error('Some error occurred while logging out', toastDefaults());
      console.error('Logout failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleProfileFormSubmit = async (file: Blob) => {
    setLoading(true);
    const formData = new FormData();
    if (file) {
      formData.append('image', file);
    }

    try {
      const url = '/user/updateUserProfilePic';
      const response = await axiosCall.put(url, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      if (response?.status === 200) {
        const user = response?.data?.user;
        const data = {
          ...userData,
          firstName: user?.firstName,
          lastName: user?.lastName,
          userEmail: user?.userEmail,
          phoneNo: user?.phoneNo,
          role: user?.role,
          address: user?.address,
          aboutUser: user?.aboutUser,
          profileImage: user?.profileImage,
          isEmailVerified: user?.isEmailVerified,
          occupation: user?.occupation,
          keepPhoneNumberPrivate: user?.keepPhoneNumberPrivate,
          keepEmailPrivate: user?.keepEmailPrivate,
          messageNotification: user?.messageNotification,
        };
        dispatch(setUserData(data));
        localStorage.setItem('user', JSON.stringify(data));
        showToastMessage(
          'Profile picture updated successfully',
          ToastType.Success
        );
        props.showHandler();
      } else {
        showToastMessage('Could not update the user', ToastType.Error);
      }
    } catch (error) {
      showToastMessage('User update failed...', ToastType.Error);
    } finally {
      setLoading(false);
    }
  };

  const updateImageHandler = async (file: Blob) => {
    await handleProfileFormSubmit(file);
  };

  const switchUserRole = (currentRole: string) => {
    const role = currentRole === 'Tenant' ? 'Owner' : 'Tenant';
    const userDetail = JSON.parse(localStorage.getItem('user') ?? '');
    userDetail.currentRole = role;
    localStorage.setItem('user', JSON.stringify(userDetail));
    dispatch(setUserData(userDetail));
  };

  if (loading) {
    return <Loader />;
  }

  if (doLogout)
    return (
      <ConfirmationPopup
        onModalClose={() => setDoLogout(false)}
        onSubmit={() => {
          handleLogout();
        }}
        confirmationText="Are you sure you want to log out?"
        saveButtonText="Yes"
        cancelButtonText="No"
        modalTitle="Logout"
        isProgress={loading}
      />
    );

  return (
    <>
      {props.showPopup && (
        <ImageEditorPopup
          src={userImage}
          showMe={props.showHandler}
          submitHandler={updateImageHandler}
          gridType="Circle"
        />
      )}
      <header
        id="dahsboard-header"
        className="d-flex align-items-center justify-content-between px-2 px-md-3 py-2 border-bottom"
      >
        {/* Left Side: Sidebar Toggle & Subscription Info */}
        <div className="d-flex align-items-center gap-2 gap-md-3">
          <button
            className="btn border-0 p-1 text-secondary d-lg-none"
            type="button"
            data-bs-toggle="offcanvas"
            data-bs-target="#FHL-Sidebar-menu"
            aria-label="Toggle navigation"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              height="24"
              viewBox="0 -960 960 960"
              width="24"
              fill="currentColor"
            >
              <path d="M140.001-254.616v-59.999h679.998v59.999H140.001Zm0-195.385v-59.998h679.998v59.998H140.001Zm0-195.384v-59.999h679.998v59.999H140.001Z" />
            </svg>
          </button>

          {hasPermission('owner') && (
            <div className="d-none d-md-flex align-items-center">
              {freeTrialDays > 0 ? (
                <div className="subscription-banner d-flex align-items-center gap-2">
                  <span
                    className="blink-text"
                    onClick={() => setShowCongratsPopup(true)}
                  >
                    {freeTrialDays > 7 ? (
                      <GiPartyPopper className="text-warning" />
                    ) : (
                      <FaTriangleExclamation className="text-warning" />
                    )}
                  </span>
                  <NavLink
                    to="/dashboard/Package"
                    className="text-decoration-none text-reset text-truncate"
                  >
                    {freeTrialDays > 7 ? (
                      <span>
                        Good news! You have <b>{freeTrialDays} days</b> left.
                      </span>
                    ) : (
                      <span>
                        Hurry up! <b>{freeTrialDays} days</b> left.
                      </span>
                    )}
                  </NavLink>
                </div>
              ) : isActive ? (
                <div className="subscription-banner d-flex align-items-center gap-2">
                  <span
                    className="subscription-icon"
                    onClick={() => setShowCongratsPopup(true)}
                  >
                    <FaCalendarCheck className="text-info" />
                  </span>
                  <NavLink
                    to="/dashboard/Package"
                    className="text-decoration-none text-reset text-truncate"
                  >
                    Expires in <b>{leftDays} days</b>.
                  </NavLink>
                </div>
              ) : (
                <div className="subscription-banner d-flex align-items-center gap-2">
                  <span
                    className="blink-text"
                    onClick={() => setShowCongratsPopup(true)}
                  >
                    <FaTriangleExclamation className="text-red" />
                  </span>
                  <NavLink
                    to="/dashboard/Package"
                    className="text-decoration-none text-reset text-truncate"
                  >
                    Package expired.
                  </NavLink>
                </div>
              )}

              {showCongratsPopup && (
                <CongratsPopup
                  freeDaysLeft={freeTrialDays}
                  daysLeft={leftDays}
                  isPlanActive={isActive}
                  onClose={() => setShowCongratsPopup(false)}
                />
              )}
            </div>
          )}
        </div>

        {/* Right Side Actions */}
        <div
          id="dashboard-header-actions"
          className="d-flex align-items-center gap-2 gap-sm-3 ms-auto"
        >
          {hasPermission('admin.owner') && !props.hideListingBtn && (
            <NavLink
              id="create-listing-btn"
              to="/dashboard/add-listing"
              className="btn btn-primary rounded-pill py-2 px-2 px-sm-3 d-flex align-items-center gap-2 shadow-sm fs-14 fw-500"
              title="Create Listing"
            >
              <svg
                height="20"
                viewBox="0 -960 960 960"
                width="20"
                fill="currentColor"
              >
                <path d="M450.001-290.001h59.998v-160h160v-59.998h-160v-160h-59.998v160h-160v59.998h160v160Zm30.066 190q-78.836 0-148.204-29.92-69.369-29.92-120.682-81.21-51.314-51.291-81.247-120.629-29.933-69.337-29.933-148.173t29.92-148.204q29.92-69.369 81.21-120.682 51.291-51.314 120.629-81.247 69.337-29.933 148.173-29.933t148.204 29.92q69.369 29.92 120.682 81.21 51.314 51.291 81.247 120.629 29.933 69.337 29.933 148.173t-29.92 148.204q-29.92 69.369-81.21 120.682-51.291 51.314-120.629 81.247-69.337 29.933-148.173 29.933ZM480-160q134 0 227-93t93-227q0-134-93-227t-227-93q-134 0-227 93t-93 227q0 134 93 227t227 93Zm0-320Z" />
              </svg>
              <span>Create Listing</span>
            </NavLink>
          )}

          {/* Message Notification Bell */}
          <NavLink
            to="/dashboard/message"
            className={`btn-notification-icon position-relative d-inline-flex align-items-center justify-content-center ${
              totalNotificationCount > 0 ? 'floating-icon' : ''
            }`}
            title="Messages & Notifications"
          >
            <svg
              height="20"
              width="20"
              viewBox="0 -960 960 960"
              fill="currentColor"
            >
              <path d="M240-400h480v-80H240v80Zm0-120h480v-80H240v80Zm0-120h480v-80H240v80ZM80-80v-720q0-33 23.5-56.5T160-880h640q33 0 56.5 23.5T880-800v480q0 33-23.5 56.5T800-240H240L80-80Zm126-240h594v-480H160v525l46-45Z" />
            </svg>

            {totalNotificationCount > 0 && (
              <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger border border-2 border-white">
                {totalNotificationCount > 99 ? '99+' : totalNotificationCount}
              </span>
            )}
          </NavLink>

          {/* User Profile Container */}
          <div className="d-flex align-items-center gap-2">
            <div className="admin-img-wrapper" onClick={props.showHandler}>
              <img
                src={userImage}
                alt="avatar"
                className="img-fluid admin-img"
              />
            </div>
            <div className="user-info-text d-none d-lg-block">
              <h6 id="user-name" className="mb-0 leading-tight">
                {userData?.firstName} {userData?.lastName}
              </h6>
              {userData?.currentRole && (
                <span className="user-role-badge">{userData?.currentRole}</span>
              )}
            </div>
          </div>

          {/* Actions Dropdown */}
          {/* Actions Dropdown */}
          <div className="dropdown position-relative">
            <button
              className="btn header-dropdown-toggle"
              type="button"
              data-bs-toggle="dropdown"
              aria-expanded="false"
              aria-label="User Options"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                height="18"
                width="18"
                viewBox="0 -960 960 960"
                fill="currentColor"
              >
                <path d="M480-360 280-560h400L480-360Z" />
              </svg>
            </button>
            <ul className="dropdown-menu dropdown-menu-end header-dropdown-menu">
              {userData?.role !== undefined && userData?.role?.length > 1 && (
                <li
                  onClick={() => switchUserRole(userData?.currentRole)}
                  className="dropdown-item"
                >
                  Switch to{' '}
                  {userData?.currentRole === 'Tenant' ? 'Owner' : 'Tenant'}
                </li>
              )}
              <li>
                <NavLink to="/dashboard/setting" className="dropdown-item">
                  Profile Settings
                </NavLink>
              </li>
              <li>
                <hr className="dropdown-divider my-1" />
              </li>
              <li>
                <a
                  onClick={() => setDoLogout(true)}
                  className="dropdown-item text-danger"
                >
                  Logout
                </a>
              </li>
            </ul>
          </div>
        </div>
      </header>
    </>
  );
}

export default ImageEditorHOC(DashboardHeader);
